import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  RefreshCw,
  UserCheck,
  UserX,
  CalendarDays,
  Wallet,
  Banknote,
  FileText,
  AlertCircle,
  Check,
} from 'lucide-react';
import { formatCurrency, formatDate, StatCard, EmptyState } from '../dashboards/shared';
import { buildApiUrl } from '../../utils/apiSetup';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
});

const normalizeStatus = (value) => (value || 'active').toString().toLowerCase();

const formatCategory = (cat) => {
  if (!cat) return '-';
  return cat.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const PensionerProfilesPanel = ({ refreshToken = 0 }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_pensioners: 0,
    active_pensioners: 0,
    stopped_pensioners: 0,
    family_pension: 0,
    retiring_this_month: 0,
  });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [bankFilter, setBankFilter] = useState('all');
  const [retirementYearFilter, setRetirementYearFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchProfiles = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch all records up to 1000 to construct dynamic drop-down choices
      const params = new URLSearchParams({
        skip: '0',
        limit: '1000',
      });
      const [listRes, statsRes] = await Promise.all([
        fetch(buildApiUrl(`/api/v1/pensioners/?${params.toString()}`), { headers: authHeaders() }),
        fetch(buildApiUrl('/api/v1/pensioners/stats'), { headers: authHeaders() }).catch(() => null),
      ]);

      const listData = await listRes.json();
      if (!listRes.ok) {
        throw new Error(listData.detail || 'Failed to load pensioner profiles');
      }

      setProfiles(listData.pensioners || []);

      if (statsRes && statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (err) {
      setError(err.message || 'Failed to load pensioner profiles');
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  useEffect(() => {
    setPage(1);
  }, [search, status, category, departmentFilter, bankFilter, retirementYearFilter]);

  const navigate = useNavigate();

  // Dynamic filter dropdown options computed from the entire dataset
  const statusOptions = useMemo(() => Array.from(new Set(profiles.map((p) => normalizeStatus(p.status)).filter(Boolean))).sort(), [profiles]);
  const categoryOptions = useMemo(() => Array.from(new Set(profiles.map((p) => p.pension_category).filter(Boolean))).sort(), [profiles]);
  const departmentOptions = useMemo(() => Array.from(new Set(profiles.map((p) => p.department).filter(Boolean))).sort(), [profiles]);
  const bankOptions = useMemo(() => Array.from(new Set(profiles.map((p) => p.bank_name).filter(Boolean))).sort(), [profiles]);
  const yearOptions = useMemo(() => Array.from(new Set(profiles.map((p) => p.retirement_year).filter(Boolean))).sort((a, b) => b - a), [profiles]);

  // Clientside Filtering & Search
  const visibleProfiles = useMemo(() => {
    const query = search.trim().toLowerCase();
    return profiles.filter((profile) => {
      const haystack = [
        profile.employee_name,
        profile.employee_code,
        profile.ppo_number,
        profile.department,
        profile.bank_name,
        profile.pension_category,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !query || haystack.includes(query);
      const matchesStatus = status === 'all' || normalizeStatus(profile.status) === status;
      const matchesCategory = category === 'all' || normalizeStatus(profile.pension_category) === category;
      const matchesDepartment = departmentFilter === 'all' || normalizeStatus(profile.department) === normalizeStatus(departmentFilter);
      const matchesBank = bankFilter === 'all' || normalizeStatus(profile.bank_name) === normalizeStatus(bankFilter);
      const matchesYear = retirementYearFilter === 'all' || String(profile.retirement_year || '') === retirementYearFilter;

      return matchesSearch && matchesStatus && matchesCategory && matchesDepartment && matchesBank && matchesYear;
    });
  }, [profiles, search, status, category, departmentFilter, bankFilter, retirementYearFilter]);

  // Clientside Local Pagination
  const paginatedProfiles = useMemo(() => {
    return visibleProfiles.slice((page - 1) * pageSize, page * pageSize);
  }, [visibleProfiles, page, pageSize]);

  const total = visibleProfiles.length;

  const exportCsv = () => {
    if (!visibleProfiles.length) {
      setSuccess('No pensioner records available to export.');
      return;
    }

    const headers = [
      'Employee Code',
      'Employee Name',
      'PPO Number',
      'Retirement Date',
      'Pension Category',
      'Basic Pension',
      'Status',
    ];
    const rows = visibleProfiles.map((profile) => [
      profile.employee_code || '',
      profile.employee_name || '',
      profile.ppo_number || '',
      profile.retirement_date || '',
      profile.pension_category || '',
      profile.basic_pension || '',
      normalizeStatus(profile.status),
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pensioner_profiles_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard title="Total Pensioners" value={stats.total_pensioners || 0} icon={Wallet} color="bg-slate-700" />
        <StatCard title="Active Pensioners" value={stats.active_pensioners || 0} icon={UserCheck} color="bg-green-600" />
        <StatCard title="Stopped Pensioners" value={stats.stopped_pensioners || 0} icon={UserX} color="bg-red-600" />
        <StatCard title="Family Pension" value={stats.family_pension || 0} icon={Banknote} color="bg-amber-600" />
        <StatCard title="Retiring This Month" value={stats.retiring_this_month || 0} icon={CalendarDays} color="bg-blue-600" />
      </div>

      <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Pensioner Profiles</h3>
            <p className="text-sm text-gray-500">Retired employees linked to live pensioner profiles.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search employee, code, PPO, department..."
                className="w-full sm:w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
              <option value="all">All Status</option>
              {statusOptions.map((opt) => <option key={opt} value={opt}>{opt.toUpperCase()}</option>)}
            </select>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
              <option value="all">All Categories</option>
              {categoryOptions.map((opt) => <option key={opt} value={opt}>{formatCategory(opt)}</option>)}
            </select>
            <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
              <option value="all">All Departments</option>
              {departmentOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
            <select value={bankFilter} onChange={(e) => setBankFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
              <option value="all">All Banks</option>
              {bankOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
            <select value={retirementYearFilter} onChange={(e) => setRetirementYearFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
              <option value="all">All Years</option>
              {yearOptions.map((option) => <option key={option} value={String(option)}>{option}</option>)}
            </select>
            <button
              type="button"
              onClick={fetchProfiles}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              type="button"
              onClick={exportCsv}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <FileText className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {(error || success) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`rounded-lg border p-4 flex items-center justify-between ${error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}
          >
            <div className="flex items-center gap-2">
              {error ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
              <p>{error || success}</p>
            </div>
            <button type="button" onClick={() => { setError(''); setSuccess(''); }}>
              <AlertCircle className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="flex justify-center py-16">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : paginatedProfiles.length === 0 ? (
        <EmptyState
          icon={Wallet}
          title="No pensioner profiles found"
          description="No retired employee pensioner profiles match the selected filter criteria."
          size="large"
        />
      ) : (
        <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left">Employee</th>
                  <th className="px-4 py-3 text-left">PPO Number</th>
                  <th className="px-4 py-3 text-left">Retirement</th>
                  <th className="px-4 py-3 text-left">Pension Start</th>
                  <th className="px-4 py-3 text-left">Basic Pension</th>
                  <th className="px-4 py-3 text-left">Bank</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedProfiles.map((profile) => (
                  <tr key={profile.id} className="hover:bg-blue-50/30">
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-semibold text-gray-900">{profile.employee_name || '-'}</p>
                        <p className="text-xs text-gray-500">{profile.employee_code || '-'}</p>
                        {profile.department && <p className="text-xs text-gray-500">{profile.department}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-700">{profile.ppo_number}</td>
                    <td className="px-4 py-4 text-gray-700">{profile.retirement_date ? formatDate(profile.retirement_date) : '-'}</td>
                    <td className="px-4 py-4 text-gray-700">{profile.pension_start_date ? formatDate(profile.pension_start_date) : '-'}</td>
                    <td className="px-4 py-4 text-gray-900 font-medium">{formatCurrency(profile.basic_pension || 0)}</td>
                    <td className="px-4 py-4 text-gray-700">
                      <div>
                        <p>{profile.bank_name || '-'}</p>
                        <p className="text-xs text-gray-500">{profile.bank_ifsc_code || ''}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-1 items-start">
                        {profile.is_deceased ? (
                          <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-700">
                            DECEASED
                          </span>
                        ) : (
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${normalizeStatus(profile.status) === 'active' ? 'bg-green-100 text-green-700' : normalizeStatus(profile.status) === 'stopped' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                            {normalizeStatus(profile.status)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        type="button"
                        onClick={() => navigate(`/pensioners/employee/${profile.employee_id}`)}
                        className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
                      >
                        <FileText className="w-4 h-4" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <p className="text-sm text-gray-600">
              Showing {paginatedProfiles.length} of {total} pensioner profile{total === 1 ? '' : 's'}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-2 border rounded-lg disabled:opacity-50 text-sm"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page * pageSize >= total}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-2 border rounded-lg disabled:opacity-50 text-sm"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PensionerProfilesPanel;
